import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  MessageButtonDef,
  MessageButtons,
  MessageDialogOptions,
  MessageWithIcon,
} from '../../models/message.model';

@Component({
  selector: 'slt-message-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, NgTemplateOutlet],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <mat-dialog-content>
      <div class="message-body">
        @if (isTemplate(data.message)) {
          <!-- Case 1: Angular TemplateRef -->
          <ng-container *ngTemplateOutlet="$any(data.message)"></ng-container>
        } @else if (isIconObject(data.message)) {
          <!-- Case 2: Icon + Message Object -->
          <div class="icon-message-layout">
            <mat-icon [color]="data.message.iconColor || 'primary'" class="dialog-icon">
              {{ data.message.icon }}
            </mat-icon>
            <div [innerHTML]="data.message.message"></div>
          </div>
        } @else {
          <!-- Case 3: Standard HTML / String Message -->
          <div [innerHTML]="data.message"></div>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      @for (btn of resolvedButtons; track $index) {
        <button
          matButton
          #actionButton
          [attr.matButtonVariant]="getButtonVariant(btn, $index)"
          [class]="getButtonClasses(btn)"
          [color]="getButtonColor(btn)"
          [disabled]="!isButtonEnabled(btn)"
          (click)="handleButtonClick(btn)"
        >
          {{ btn.label }}
        </button>
      }
    </mat-dialog-actions>
  `,
  styles: [
    `
      .message-body {
        padding: 0.5rem 0;
        line-height: 1.5;
        word-break: break-word;
        color: var(--mat-sys-on-surface-variant, #555);
      }

      .icon-message-layout {
        display: flex;
        align-items: flex-start;
        gap: 1rem;

        .dialog-icon {
          font-size: 28px;
          width: 28px;
          height: 28px;
          flex-shrink: 0;
          margin-top: 2px;
        }
      }

      .destructive-btn {
        background-color: #b71c1c !important;
        color: #fff !important;
      }
    `,
  ],
})
export class MessageDialogComponent {
  readonly dialogRef = inject(MatDialogRef<MessageDialogComponent>);
  readonly data: MessageDialogOptions = inject(MAT_DIALOG_DATA);

  // Query all rendered action buttons from the template
  @ViewChildren('actionButton', { read: ElementRef }) actionButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >;

  constructor() {
    afterNextRender(() => {
      setTimeout(() => {
        const buttonsArray = this.actionButtons?.toArray();
        const definitions = this.resolvedButtons;

        if (buttonsArray && buttonsArray.length > 0) {
          let targetIndex = definitions.findIndex((b) => b.isFocused && this.isButtonEnabled(b));

          if (targetIndex === -1) {
            targetIndex = definitions.findIndex(
              (b) => (b.isPrimary || b.isDestructive) && this.isButtonEnabled(b),
            );
          }

          if (targetIndex === -1) {
            targetIndex = buttonsArray.length - 1;
          }

          const targetButton = buttonsArray[targetIndex];
          if (targetButton?.nativeElement) {
            targetButton.nativeElement.focus();
          }
        }
      }, 50);
    });
  }

  get resolvedButtons(): MessageButtonDef[] {
    const buttonsConfig = this.data.buttons;

    if (Array.isArray(buttonsConfig)) {
      return buttonsConfig as MessageButtonDef[];
    }

    if (buttonsConfig === MessageButtons.Close) {
      return [
        {
          label: 'Close',
          value: 'close',
          isPrimary: true,
          closeOnClick: true,
        },
      ];
    }

    if (buttonsConfig === MessageButtons.YesNoCancel) {
      return [
        {
          label: 'Cancel',
          value: 'cancel',
          closeOnClick: true,
        },
        {
          label: 'No',
          value: 'no',
          closeOnClick: true,
        },
        {
          label: 'Yes',
          value: 'yes',
          isPrimary: true,
          closeOnClick: true,
          isFocused: true,
        },
      ];
    }

    return [
      {
        label: 'No',
        value: 'no',
        closeOnClick: true,
      },
      {
        label: 'Yes',
        value: 'yes',
        isPrimary: true,
        closeOnClick: true,
        isFocused: true,
      },
    ];
  }

  isTemplate(value: any): value is TemplateRef<any> {
    return value instanceof TemplateRef;
  }

  isIconObject(value: any): value is MessageWithIcon {
    return value !== null && typeof value === 'object' && 'icon' in value && 'message' in value;
  }

  isButtonEnabled(btn: MessageButtonDef): boolean {
    if (btn.isEnabled === undefined) return true;
    return typeof btn.isEnabled === 'function' ? btn.isEnabled() : btn.isEnabled;
  }

  getButtonVariant(btn: MessageButtonDef, index: number): string {
    if (btn.isPrimary || btn.isDestructive || this.isDefaultPrimary(btn, index)) {
      return 'filled';
    }
    return 'text';
  }

  getButtonColor(btn: MessageButtonDef): string {
    if (btn.isDestructive) {
      return '';
    }
    return 'primary';
  }

  isDefaultPrimary(btn: MessageButtonDef, index: number): boolean {
    const buttons = this.resolvedButtons;
    const hasAnyStylingFlag = buttons.some((b) => b.isPrimary || b.isDestructive);
    return !hasAnyStylingFlag && index === buttons.length - 1;
  }

  getButtonClasses(btn: MessageButtonDef): string {
    const classes: string[] = [];
    if (btn.isDestructive) {
      classes.push('destructive-btn');
    }
    if (btn.cssClass) {
      if (Array.isArray(btn.cssClass)) {
        classes.push(...btn.cssClass);
      } else {
        classes.push(btn.cssClass);
      }
    }
    return classes.join(' ');
  }

  handleButtonClick(btn: MessageButtonDef) {
    if (!this.isButtonEnabled(btn)) return;
    const resolvedValue = typeof btn.value === 'function' ? btn.value() : btn.value;
    const shouldClose = btn.closeOnClick ?? true;
    if (shouldClose) {
      this.dialogRef.close(resolvedValue);
    }
  }
}
